import { TextSizes } from '@picsart/growth-rc/Title';
import { AllowedTextSizes } from '@picsart/growth-rc/types/lib/atoms/Description';
import { useTranslator } from '@picsart/rc/services/localization';
import cls from 'classnames';
import Color from 'color';

import TitleWithDescription from 'components/Colors/TitleWithDescription';
import Row from './Row';
import { ColorTableProps } from './types';

import useStyles from './styles';
import { getDescriptionText, hexColorToBinary, xyzToHunterLab, xyzToLuv, xyzToXyy } from './utils';

const DECIMALS = 3;

function ColorTable({ className, colorHex, name }: ColorTableProps) {
  const classes = useStyles();
  const translate = useTranslator();
  const prefixedHex = colorHex;
  const color = Color(prefixedHex);
  const rgb = color.rgb().object();
  const rgbPercent = color.percentString().match(/rgb\((.*)\)/)[1];
  const cmyk = color.cmyk().round().object();
  const hsl = color.hsl().object();
  const hsv = color.hsv().object();
  const lab = color.lab().object();
  const xyz = color.xyz().object();
  const xyy = xyzToXyy({ x: xyz.x, y: xyz.y, z: xyz.z });
  const lch = color.lch().object();
  const luv = xyzToLuv({ x: xyz.x, y: xyz.y, z: xyz.z });
  const hLab = xyzToHunterLab({ x: xyz.x, y: xyz.y, z: xyz.z });

  return (
    <div className={cls(classes.root, className)}>
      <TitleWithDescription
        className={classes.text}
        title={{
          id: 1,
          text: `${name} color conversion table`,
          tag: 'h2',
          size: TextSizes.Auto,
        }}
        description={{
          id: 2,
          text: translate(getDescriptionText(colorHex)),
          size: 'large' as AllowedTextSizes,
        }}
      />
      <div className={classes.tableContainer}>
        <table className={classes.table}>
          <thead>
            <tr>
              <th>{translate('Color code')}</th>
              <th>{translate('Value')}</th>
              <th>CSS</th>
            </tr>
          </thead>

          <tbody>
            <Row data={['HEX', colorHex, prefixedHex]} />
            <Row
              data={[
                translate('RGB DECIMAL'),
                `${rgb.r}, ${rgb.g}, ${rgb.b}`,
                `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`,
              ]}
            />
            <Row data={[translate('RGB PERCENTAGE'), rgbPercent, rgbPercent.replaceAll('%', '')]} />

            <Row data={['CMYK', `${cmyk.c}, ${cmyk.m}, ${cmyk.y}, ${cmyk.k}`, '']} />
            <Row
              data={[
                'HSL',
                `${hsl.h.toFixed(1)}°, ${hsl.s.toFixed(1)}, ${hsl.l.toFixed(1)}`,
                `hsl(${hsl.h.toFixed(1)}°, ${hsl.s.toFixed(1)}%, ${hsl.l.toFixed(1)}%)`,
              ]}
            />
            <Row
              data={[
                translate('HSV (OR HSB)'),
                `${hsv.h.toFixed(1)}°, ${hsv.s.toFixed(1)}, ${hsv.v.toFixed(1)}`,
                '',
              ]}
            />

            <Row data={[translate('WEB SAFE'), colorHex, prefixedHex]} />

            <Row
              data={[
                'CIE-LAB',
                `${lab.l.toFixed(DECIMALS)}, ${lab.a.toFixed(DECIMALS)}, ${lab.b.toFixed(
                  DECIMALS,
                )}`,
                '',
              ]}
            />

            <Row
              data={[
                'XYZ',
                `${xyz.x.toFixed(DECIMALS)}, ${xyz.y.toFixed(DECIMALS)}, ${xyz.z.toFixed(
                  DECIMALS,
                )}`,
                '',
              ]}
            />
            <Row
              data={[
                'XYY',
                `${xyy[0].toFixed(DECIMALS)}, ${xyy[1].toFixed(DECIMALS)}, ${xyy[2].toFixed(
                  DECIMALS,
                )}`,
                '',
              ]}
            />

            <Row
              data={[
                'CIE-LCH',
                `${lch.l.toFixed(DECIMALS)}, ${lch.c.toFixed(DECIMALS)}, ${lch.h.toFixed(
                  DECIMALS,
                )}`,
                '',
              ]}
            />

            <Row
              data={[
                'CIE-LUV',
                `${luv.l.toFixed(DECIMALS)}, ${luv.u.toFixed(DECIMALS)}, ${luv.v.toFixed(
                  DECIMALS,
                )}`,
                '',
              ]}
            />

            <Row
              data={[
                'HUNTER-LAB',
                `${hLab.l.toFixed(DECIMALS)}, ${hLab.a.toFixed(DECIMALS)}, ${hLab.b.toFixed(
                  DECIMALS,
                )}`,
                '',
              ]}
            />

            <Row data={[translate('BINAR'), hexColorToBinary(colorHex), '']} />
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ColorTable;
